import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { MiniFacesheetContainer } from "../components/MiniFacesheet/MiniFacesheet";
import { downloadGeneratedPdfs, downloadGeneratedPdf } from "../services/fileDownloader";
import * as pdfActions from "../actions/pdfAction";
import { LoadingBar } from "../components/LoadingComponents/LoadingBar";
import * as documentActions from "../actions/documentActions";
import { DispatchInstructionList, PdfItem } from "../components/DispatchComponents";
import * as personActions from "../actions/personActions";
import * as contractService from "../services/contractService";
import { Link } from "react-router-dom";
import { Birdseed, TooltipMessage, TooltipTrigger } from "@coverme/elements";
import moment from "moment";
import * as pdfService from "../services/pdfService";
import { Icon } from "@coverme/icons";
import "../../src/styles/dispatch.scss";
import { sendForDigitalSignature, sendForDigitalSignatureMulti } from "../services/digitalSignatureService";
import { toast } from "react-toastify";
import { DigitalSignatureContactDetails } from "../components/DispatchComponents/DigitalSignatureContactDetails";
import CustomerEnabledFeature from "../components/CustomerEnabledFeature/CustomerEnabledFeature";
import { LabelledCheckbox } from "../components/FormComponents/Checkbox";
import { Tooltip } from "../components/Tooltips/Tooltip";
import * as tagsAction from "../actions/tagsActions";
import { SimpleSingleSelect } from "../components/FormComponents/SimpleSingleSelect";
import { noop } from "lodash-es";
import { CTA } from "@coverme/forms";
import * as faxService from "../services/faxService";
import { getFriendlyDateFormat } from "../services/dateFormatter";

export const DispatchComponent = ({
    customerCode,
    encounterId,
    pdfList,
    getPdfs,
    history,
    faxHistory,
    isPdfLoading,
    interview,
    isHouseholdLoading,
    isProgramsLoading,
    programs,
    resetPdfs,
    customerJurisdictions,
    printedPdfList,
    shouldShowUmmcNavigation,
    signedPdfList,
    customerId,
    getEncounterTag,
}) => {
    const isInterviewLoading = encounterId !== interview.encounterId;
    const [checkedCards, setCheckedCards] = useState({});
    const [isSecureFaxSending, setIsSecureFaxSending] = useState(false);
    const [selectedAssistanceCategories, setSelectedAssistanceCategories] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isSignatureRequestBeingSent, setIsSignatureRequestBeingSent] = useState(false);
    const [isSignatureDetailsBeingRequested, setIsSignatureDetailsBeingRequested] = useState(false);
    const [doesAnyCheckedPdfNeedSignature, setDoesAnyCheckedPdfNeedSignature] = useState(false);
    const [signatureRequestDetails, setSignatureRequestDetails] = useState({
        id: null,
        fileName: null,
        mode: null,
    });
    const [signatureRequestContactDetails, setSignatureRequestContactDetails] = useState({
        email: "",
        alternateEmail: "",
        phoneNumber: "",
        alternatePhoneNumber: "",
        titleText: "",
        modeText: "",
        displayContact: "",
        alternateDisplayContact: "",
    });
    const [doesAnyPdfNeedSignature, setDoesAnyPdfNeedSignature] = useState(false);
    const [isSelectAllChecked, setIsSelectAllChecked] = useState(true);

    const responsibleParty = interview.entry.isResponsiblePartyDifferentFromPatient
        ? interview.entry.responsibleParty
        : interview.entry.patient;

    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "end",
            inline: "nearest",
        });
    };

    const [selectedMode, setSelectedMode] = useState("");
    useEffect(() => {
        if (pdfList?.length > 0) {
            let filteredPdfs = pdfList.filter(y => y.isRequiredForACase === true);
            const checkedCards = filteredPdfs?.map(x => x.key);
            setCheckedCards(checkedCards);
            const selectedCategoriesObj = programs.data.filter(
                x =>
                    x.eligibilityDeterminationStatus.name === "Action Complete" &&
                    x.eligibilityStatus.name === "Approved"
            );

            if (selectedCategoriesObj.filter(x => x.name.toLowerCase().indexOf("medicaid") >= 0).length > 0) {
                setDoesAnyPdfNeedSignature(pdfList.filter(e => e.doesNeedDigitalSignature === true).length > 0);
            }
            setIsSelectAllChecked(true);
        }
    }, [pdfList, printedPdfList]);

    const formattedPdfList = useMemo(() => {
        const removeYearsFromFederal = /20[0-9]{2} Federal/;
        let formattedList = pdfList?.map(x => {
            return {
                ...x,
                name: x.name.replace(removeYearsFromFederal, "Federal"),
            };
        });

        return formattedList?.filter(x => x.isRequiredForACase === true);
    }, [pdfList, printedPdfList]);

    function onDownload(id, fileName) {
        downloadGeneratedPdf(encounterId, id, fileName, false)
            .then(() => {
                setIsDownloading(false);
                const selectedCategoriesObj = programs.data.filter(
                    x =>
                        x.eligibilityDeterminationStatus.name === "Action Complete" &&
                        x.eligibilityStatus.name === "Approved"
                );
                let selectedCategories = pdfService.getSelectedCategories(programs, interview);
                let jurisdictions = pdfService.getJurisdiction(selectedCategoriesObj, customerJurisdictions);
                getPdfs(
                    selectedCategories,
                    customerCode,
                    encounterId,
                    customerId,
                    new Date(interview.created),
                    jurisdictions
                );
            })
            .catch(() => {
                setIsDownloading(false);
            });
    }

    useEffect(() => {
        if (isProgramsLoading) {
            return;
        }

        if (isInterviewLoading) {
            return;
        }

        let selectedCategoriesObj = programs.data.filter(
            x => x.eligibilityDeterminationStatus.name === "Action Complete" && x.eligibilityStatus.name === "Approved"
        );
        if (selectedCategoriesObj.length === 0) {
            selectedCategoriesObj = programs.data.filter(
                x =>
                    x.eligibilityDeterminationStatus.name === "Pending Decision" &&
                    x.eligibilityStatus.name === "Pending"
            );
            if (programs?.data?.length === 1) {
                let deniedPrograms = programs.data.filter(
                    x =>
                        x.eligibilityDeterminationStatus.name === "Action Complete" &&
                        x.eligibilityStatus.name === "Ineligibile" &&
                        x.reasonForIneligibility === "Denied"
                );
                if (deniedPrograms.length === 1) {
                    selectedCategoriesObj = deniedPrograms;
                }
            }
        }
        let selectedCategories = pdfService.getSelectedCategories(programs, interview);
        let jurisdictions = pdfService.getJurisdiction(selectedCategoriesObj, customerJurisdictions);
        setSelectedAssistanceCategories(selectedCategoriesObj);
        getPdfs(selectedCategories, customerCode, encounterId, customerId, new Date(interview.created), jurisdictions);
        return () => {
            resetPdfs();
        };

        getEncounterTag(encounterId);
    }, [programs, interview]);

    const onSelectedPdfsUpdated = item => {
        setCheckedCards({
            ...checkedCards,
            ...item,
        });
    };

    const setAlternateEmail = () => {
        if (
            signatureRequestContactDetails?.alternateEmail === "" ||
            signatureRequestContactDetails?.alternateEmail === null
        ) {
            signatureRequestContactDetails.alternateEmail = interview?.entry?.patient?.email;
        }
    };

    const onSubmit = mode => {
        setIsDownloading(true);
        if (pdfList?.length === 0) {
            history.push("/");
            return;
        }

        let pdfsToGenerate = pdfList?.filter(y => y.doesNeedDigitalSignature === true);
        pdfsToGenerate = pdfsToGenerate.map(x => x.key);

        if (mode === "print") {
            if (pdfsToGenerate.length > 0) {
                downloadGeneratedPdfs(encounterId, pdfsToGenerate, "GeneratePdf.pdf", false)
                    .then(() => {
                        setIsDownloading(false);
                        const selectedCategoriesObj = programs.data.filter(
                            x =>
                                x.eligibilityDeterminationStatus.name === "Action Complete" &&
                                x.eligibilityStatus.name === "Approved"
                        );
                        let selectedCategories = pdfService.getSelectedCategories(programs, interview);
                        let jurisdictions = pdfService.getJurisdiction(selectedCategoriesObj, customerJurisdictions);
                        getPdfs(
                            selectedCategories,
                            customerCode,
                            encounterId,
                            customerId,
                            new Date(interview.created),
                            jurisdictions
                        );
                    })
                    .catch(() => {
                        setIsDownloading(false);
                    });
            }
        } else {
            setIsSignatureRequestBeingSent(true);
            setAlternateEmail();
            sendForDigitalSignatureMulti(
                encounterId,
                customerId,
                pdfsToGenerate,
                signatureRequestDetails.mode,
                signatureRequestContactDetails.alternateEmail,
                signatureRequestContactDetails.alternatePhoneNumber
            )
                .then(r => {
                    onSendForSignatureSuccess(r, mode);
                    setSelectedMode("");
                })
                .catch(err => {
                    onSendForSignatureFailure();
                    // setSelectedMode("");
                });
        }
    };

    function onSendForSignatureSuccess(r, mode) {
        setIsSignatureDetailsBeingRequested(false);
        setIsSignatureRequestBeingSent(false);
        if (mode === "link") {
            const newWindow = window.open(r?.data, "_blank", "noopener,noreferrer");
            if (newWindow) newWindow.opener = null;
        } else {
            toast.success("File shared successfully.");
        }
    }

    function onSendForSignature(id, fileName, mode) {
        let isMultiDocument = false;
        if (signatureRequestDetails.fileName !== null && signatureRequestDetails.id !== null) {
            isMultiDocument = false;
        } else {
            isMultiDocument = true;
        }
        setAlternateEmail();
        if (isMultiDocument === true) {
            onSubmit(mode);
        } else {
            setIsSignatureRequestBeingSent(true);
            sendForDigitalSignature(
                encounterId,
                customerId,
                signatureRequestDetails.id,
                signatureRequestDetails.fileName,
                signatureRequestDetails.mode,
                signatureRequestContactDetails.alternateEmail,
                signatureRequestContactDetails.alternatePhoneNumber
            )
                .then(r => {
                    onSendForSignatureSuccess(r, mode);
                })
                .catch(err => {
                    onSendForSignatureFailure();
                });
        }
    }

    function onSendForSignatureFailure() {
        toast.error("File share unsuccessful.");
        setIsSignatureRequestBeingSent(false);
    }

    function confirmDetailsBeforeSignatureRequest(id, fileName, mode, isMultiDocument = false) {
        if (
            (responsibleParty.ssn === "" || responsibleParty.ssn === null || responsibleParty.ssn === undefined) &&
            mode !== "link"
        ) {
            return;
        }
        if (isMultiDocument === false) {
            setSignatureRequestDetails({ id: id, fileName: fileName, mode: mode });
        } else {
            setSignatureRequestDetails({ id: null, fileName: null, mode: mode });
        }
        scrollToBottom();

        setIsSignatureDetailsBeingRequested(true);
        setIsSignatureRequestBeingSent(false);
        let titleText = "";
        let modeText;
        let displayContact = "";
        let alternateDisplayContact = "";
        if (signatureRequestContactDetails.email === null || signatureRequestContactDetails.email === "") {
            alternateDisplayContact = "";
            if (responsibleParty.email === null || responsibleParty.email === "") {
                displayContact = "N/A";
            } else {
                displayContact = responsibleParty.email;
            }
        } else {
            displayContact = signatureRequestContactDetails.email;
        }

        if (mode === "email") {
            titleText = "Document link will be sent to the following Email, please verify.";
            modeText = "Email ID";
        } else if (mode === "link") {
            titleText = "Email ID is required for collecting signature on device, please verify.";
            modeText = "Email ID";
        } else if (mode === "phone") {
            if (
                signatureRequestContactDetails.phoneNumber === null ||
                signatureRequestContactDetails.phoneNumber === ""
            ) {
                alternateDisplayContact = "";
                if (
                    responsibleParty.primaryPhoneNumber?.phoneNumber === null ||
                    responsibleParty.primaryPhoneNumber?.phoneNumber === ""
                ) {
                    displayContact = "N/A";
                } else {
                    displayContact = responsibleParty.primaryPhoneNumber?.phoneNumber;
                }
            } else {
                displayContact = signatureRequestContactDetails.phoneNumber;
            }
            titleText = "Document link will be sent to the following Phone Number, please verify.";
            modeText = "Phone Number";
        }
        setSignatureRequestContactDetails({
            email: responsibleParty.email,
            alternateEmail: signatureRequestContactDetails.email,
            phoneNumber: responsibleParty.primaryPhoneNumber?.phoneNumber,
            alternatePhoneNumber: signatureRequestContactDetails.phoneNumber,
            titleText: titleText,
            modeText: modeText,
            displayContact: displayContact,
            alternateDisplayContact: alternateDisplayContact,
        });
    }

    function updateInputValue(evt) {
        setSignatureRequestContactDetails({
            ...signatureRequestContactDetails,
            alternateEmail:
                signatureRequestDetails.mode !== "phone"
                    ? evt.target.value
                    : signatureRequestContactDetails.alternateEmail,
            alternateDisplayContact: evt.target.value,
            alternatePhoneNumber:
                signatureRequestDetails.mode === "phone"
                    ? evt.target.value
                    : signatureRequestContactDetails.alternatePhoneNumber,
        });
    }

    const onSelectAllChanged = () => {
        if (pdfList?.length > 0) {
            var checkedCards;
            pdfList
                .filter(x => x.isRequiredForACase === true)
                .forEach(x => {
                    checkedCards = {
                        ...checkedCards,
                        [x.key]: !isSelectAllChecked,
                    };
                });
            setIsSelectAllChecked(!isSelectAllChecked);
            setCheckedCards(checkedCards);
        }
    };
    const getSelectedPdfs = () => {
        if (pdfList?.length > 0) {
            let selectedPdfs = [];

            for (const pdf of pdfList) {
                if (checkedCards[pdf.key] !== false) {
                    selectedPdfs.push(pdf);
                }
            }
            return selectedPdfs?.filter(y => y.isRequiredForACase === true);
        }
    };
    useEffect(() => {
        let selectedPdfs = getSelectedPdfs();
        console.log({ selectedPdfs });
        console.log({ formattedPdfList });
        console.log({ checkedCards });
        console.log({ pdfList });

        if (pdfList?.length > 0) {
            selectedPdfs?.length == formattedPdfList?.length
                ? setIsSelectAllChecked(true)
                : setIsSelectAllChecked(false);
        }
        if (selectedPdfs !== undefined && selectedPdfs !== null) {
            const selectedCategoriesObj = programs.data.filter(
                x =>
                    x.eligibilityDeterminationStatus.name === "Action Complete" &&
                    x.eligibilityStatus.name === "Approved"
            );
            setDoesAnyCheckedPdfNeedSignature(selectedPdfs.filter(e => e.doesNeedDigitalSignature === true).length > 0);
        }
    }, [checkedCards]);

    async function onSecureFaxSend() {
        setIsSecureFaxSending(true);
        const lastSignedPdf = signedPdfList[signedPdfList.length - 1];
        console.debug({ signedPdfList, lastSignedPdf });
        try {
            await faxService.sendFax(lastSignedPdf.documentId);
            toast.success("Fax sent successfully");
        } catch (e) {
            toast.error("Sending fax failed");
        } finally {
            setIsSecureFaxSending(false);
        }

        const selectedCategoriesObj = programs.data.filter(
            x => x.eligibilityDeterminationStatus.name === "Action Complete" && x.eligibilityStatus.name === "Approved"
        );
        let selectedCategories = pdfService.getSelectedCategories(programs, interview);
        let jurisdictions = pdfService.getJurisdiction(selectedCategoriesObj, customerJurisdictions);
        getPdfs(selectedCategories, customerCode, encounterId, customerId, new Date(interview.created), jurisdictions);
    }

    return (
        <LoadingBar isLoading={isPdfLoading || isInterviewLoading || isHouseholdLoading}>
            <div>
                <Birdseed>
                    <Link to={"/"}>Home</Link>
                    <a href="javascript:void(0)">
                        Interview / <strong>Forms & Applications</strong>{" "}
                    </a>
                </Birdseed>
                <div className="default-page" id="dispatch">
                    <MiniFacesheetContainer />
                    <div className="content">
                        <div id="header-section">
                            <h2 className="page-header">Forms & Applications</h2>
                        </div>

                        {selectedAssistanceCategories !== null && (
                            <DispatchInstructionList
                                encounterId={encounterId}
                                programs={selectedAssistanceCategories}
                                shouldShowUmmcNavigation={shouldShowUmmcNavigation}
                            />
                        )}
                        <div id="pdf-list" style={{ position: "relative" }}>
                            <LoadingBar isLoading={isPdfLoading}>
                                {pdfList?.length === 0 && <h3 className="no-forms-found">No forms found</h3>}
                                {pdfList?.length !== 0 && (
                                    <div style={{ display: "flex", marginLeft: "20px", alignItems: "center" }}>
                                        <LabelledCheckbox
                                            customWidth={`30%`}
                                            labelText={isSelectAllChecked ? `Select None` : `Select All`}
                                            id={`selectAllCkBox`}
                                            value={`selectAllCkBox`}
                                            checked={isSelectAllChecked === true}
                                            groupName="selectAllCkBox"
                                            onChange={onSelectAllChanged}
                                        />
                                        <h3 style={{ width: "35%", marginLeft: "40%" }}>Print Date</h3>
                                        <div style={{ marginLeft: "auto", marginRight: "16px" }}>
                                            <button className="download-button" onClick={onSubmit.bind(this, "print")}>
                                                <Icon icon={"download"} color={"black"} height={25} width={25} />
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {formattedPdfList?.map(x => (
                                    <PdfItem
                                        encounterId={encounterId}
                                        key={x.key}
                                        fileName={x.name}
                                        isChecked={checkedCards[x.key] == null ? true : checkedCards[x.key]}
                                        id={x.key}
                                        onChange={onSelectedPdfsUpdated}
                                        onDownload={onDownload}
                                        doesNeedDigitalSignature={x.doesNeedDigitalSignature}
                                        onSendForSignature={onSendForSignature}
                                        confirmDetailsBeforeSignatureRequest={confirmDetailsBeforeSignatureRequest}
                                        printDate={
                                            printedPdfList?.filter(y => y.templateKey === x.key)[0]?.printDate
                                                ? moment(
                                                      printedPdfList?.filter(y => y.templateKey === x.key)[0]?.printDate
                                                  ).format("MM.DD.YYYY")
                                                : "Not Printed"
                                        }
                                        isDigitalSignatureDisabled={
                                            interview.entry.patient.ssn === "" ||
                                            interview.entry.patient.ssn === null ||
                                            interview.entry.patient.ssn === undefined
                                        }
                                    />
                                ))}
                                {doesAnyPdfNeedSignature === true &&
                                    responsibleParty.ssn != "" &&
                                    responsibleParty.ssn != null &&
                                    responsibleParty.ssn != undefined &&
                                    signedPdfList?.length === 0 && (
                                        <>
                                            <CustomerEnabledFeature
                                                requiredFeature="Digital Signature"
                                                shouldHide={true}
                                            >
                                                <div
                                                    className="multi-pdf-actions-container"
                                                    style={{ justifyContent: "flex-start" }}
                                                >
                                                    <div className={`${doesAnyCheckedPdfNeedSignature === true}`}>
                                                        <div>
                                                            <div style={{ display: "flex" }}>
                                                                <p
                                                                    style={{
                                                                        fontSize: "1.6rem",
                                                                        marginBottom: "-15px",
                                                                        marginTop: "5px",
                                                                    }}
                                                                >
                                                                    <h3
                                                                        style={{
                                                                            fontStyle: "normal",
                                                                            fontWeight: "bold",
                                                                            display: "flex",
                                                                            gap: "25px",
                                                                        }}
                                                                    >
                                                                        <div>Send for Digital Signature</div>
                                                                        <Tooltip>
                                                                            <TooltipTrigger>
                                                                                <Icon icon="tooltip" />
                                                                            </TooltipTrigger>
                                                                            <TooltipMessage>
                                                                                <p>
                                                                                    Eligible documents requiring a
                                                                                    signature can be sent to a patient
                                                                                    via email or mobile phone.
                                                                                </p>
                                                                            </TooltipMessage>
                                                                        </Tooltip>
                                                                    </h3>
                                                                    Select a method below: Required documents will be
                                                                    sent for Digital Signature.
                                                                </p>
                                                            </div>

                                                            <div style={{ width: "300px", marginBottom: "15px" }}>
                                                                <SimpleSingleSelect
                                                                    id="communicationTypes"
                                                                    options={["Email", "Phone"]}
                                                                    setFieldTouched={noop}
                                                                    name={"communicationTypes"}
                                                                    setFieldValue={(e, v) => {
                                                                        setSelectedMode(v);
                                                                    }}
                                                                    onFieldChange={e => {
                                                                        var communicationType = "";
                                                                        if (e == "Email") {
                                                                            communicationType = "email";
                                                                        }
                                                                        if (e == "Phone") {
                                                                            communicationType = "phone";
                                                                        }
                                                                        confirmDetailsBeforeSignatureRequest(
                                                                            this,
                                                                            null,
                                                                            communicationType,
                                                                            true
                                                                        );
                                                                    }}
                                                                    value={selectedMode}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div>
                                                    {isSignatureDetailsBeingRequested === true && (
                                                        <DigitalSignatureContactDetails
                                                            signatureRequestDetails={signatureRequestDetails}
                                                            signatureRequestContactDetails={
                                                                signatureRequestContactDetails
                                                            }
                                                            onSendForSignature={onSendForSignature}
                                                            updateInputValue={updateInputValue}
                                                            isSignatureRequestBeingSent={isSignatureRequestBeingSent}
                                                            titleText={signatureRequestContactDetails.titleText}
                                                            modeText={selectedMode}
                                                        />
                                                    )}
                                                    <div ref={messagesEndRef} />
                                                </div>
                                            </CustomerEnabledFeature>
                                        </>
                                    )}

                                {doesAnyPdfNeedSignature === true &&
                                    (responsibleParty.ssn === "" || responsibleParty.ssn == null) &&
                                    signedPdfList?.length === 0 && (
                                        <>
                                            <CustomerEnabledFeature
                                                requiredFeature="Digital Signature"
                                                shouldHide={true}
                                            >
                                                <div
                                                    className="multi-pdf-actions-container"
                                                    style={{ justifyContent: "flex-start" }}
                                                >
                                                    <div className={`${doesAnyCheckedPdfNeedSignature === true}`}>
                                                        <div>
                                                            <div style={{ display: "flex" }}>
                                                                <p
                                                                    style={{
                                                                        fontSize: "1.6rem",
                                                                        marginBottom: "-15px",
                                                                        marginTop: "5px",
                                                                    }}
                                                                >
                                                                    <h3
                                                                        style={{
                                                                            fontStyle: "normal",
                                                                            fontWeight: "bold",
                                                                            display: "flex",
                                                                            gap: "25px",
                                                                        }}
                                                                    >
                                                                        Send for Digital Signature
                                                                        <Tooltip>
                                                                            <TooltipTrigger>
                                                                                <Icon icon="tooltip" />
                                                                            </TooltipTrigger>
                                                                            <TooltipMessage>
                                                                                <p>
                                                                                    Eligible documents requiring a
                                                                                    signature can be sent to a patient
                                                                                    via email or mobile phone.
                                                                                </p>
                                                                            </TooltipMessage>
                                                                        </Tooltip>
                                                                    </h3>
                                                                    This case isn’t eligible for Digital Signature as
                                                                    SSN isn’t collected for the patient.
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </CustomerEnabledFeature>
                                        </>
                                    )}

                                {doesAnyPdfNeedSignature === true &&
                                    responsibleParty.ssn != "" &&
                                    responsibleParty.ssn != null &&
                                    responsibleParty.ssn != undefined &&
                                    signedPdfList?.length > 0 && (
                                        <>
                                            <CustomerEnabledFeature
                                                requiredFeature="Digital Signature"
                                                shouldHide={true}
                                            >
                                                <div
                                                    className="multi-pdf-actions-container"
                                                    style={{ justifyContent: "flex-start" }}
                                                >
                                                    <div className={`${doesAnyCheckedPdfNeedSignature === true}`}>
                                                        <div>
                                                            <div style={{ display: "flex" }}>
                                                                <p
                                                                    style={{
                                                                        fontSize: "1.6rem",
                                                                        marginBottom: "-15px",
                                                                        marginTop: "5px",
                                                                    }}
                                                                >
                                                                    <h3
                                                                        style={{
                                                                            fontStyle: "normal",
                                                                            fontWeight: "bold",
                                                                            display: "flex",
                                                                            gap: "25px",
                                                                        }}
                                                                    >
                                                                        Send for Digital Signature
                                                                        <Tooltip>
                                                                            <TooltipTrigger>
                                                                                <Icon icon="tooltip" />
                                                                            </TooltipTrigger>
                                                                            <TooltipMessage>
                                                                                <p>
                                                                                    Eligible documents requiring a
                                                                                    signature can be sent to a patient
                                                                                    via email or mobile phone.
                                                                                </p>
                                                                            </TooltipMessage>
                                                                        </Tooltip>
                                                                    </h3>
                                                                    {signedPdfList?.length > 0 &&
                                                                        signedPdfList?.filter(
                                                                            x => x.status === "Completed"
                                                                        )?.length === 0 && (
                                                                            <>
                                                                                Sent for Digital Signature on{" "}
                                                                                {moment
                                                                                    .utc(signedPdfList[0].initiatedDate)
                                                                                    .format("MM/DD/YYYY")}
                                                                            </>
                                                                        )}

                                                                    {signedPdfList?.length > 0 &&
                                                                        signedPdfList?.filter(
                                                                            x => x.status === "Completed"
                                                                        )?.length > 0 &&
                                                                        faxHistory.length === 0 && (
                                                                            <>
                                                                                Digital Signature received on{" "}
                                                                                {moment
                                                                                    .utc(
                                                                                        signedPdfList[0]
                                                                                            .signatureCollectionDate
                                                                                    )
                                                                                    .format("MM/DD/YYYY")}
                                                                            </>
                                                                        )}

                                                                    {signedPdfList?.length > 0 &&
                                                                        signedPdfList?.filter(
                                                                            x => x.status === "Completed"
                                                                        )?.length > 0 &&
                                                                        faxHistory.length > 0 && (
                                                                            <>
                                                                                Secure Fax sent on{" "}
                                                                                {moment
                                                                                    .utc(faxHistory[0].created)
                                                                                    .format("MM/DD/YYYY")}
                                                                            </>
                                                                        )}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </CustomerEnabledFeature>
                                        </>
                                    )}

                                {signedPdfList?.length > 0 &&
                                    signedPdfList?.filter(x => x.status === "Completed")?.length > 0 &&
                                    faxHistory.length === 0 && (
                                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                            <CTA
                                                styleType={"blue"}
                                                ctaType={"button"}
                                                click={onSecureFaxSend}
                                                loading={isSecureFaxSending}
                                            >
                                                Send Secure Fax
                                            </CTA>
                                        </div>
                                    )}
                                {/*                                {signedPdfList?.length > 0 && faxHistory.length > 0 && (
                                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                      Fax Sent on
                                  </div>
                                )}*/}
                            </LoadingBar>
                        </div>
                    </div>
                </div>
            </div>
        </LoadingBar>
    );
};

export const DispatchContainer = connect(
    (state, ownProps) => {
        const patientId = state.interview?.entry?.patient?.personId;
        let underlyingPerson = null;
        if (patientId) {
            underlyingPerson = state.person.data[patientId];
        }
        return {
            programs: state.programs,
            isProgramsLoading: state.programs.isLoading === true || state.programs.isInitialized === false,
            customerCode: state.customer.data?.customer?.code,
            customerId: state.customer.data?.customer?.GlobalId,
            isPdfLoading: state.pdfs.isLoading === true || state.pdfs.isInitialized === false,
            pdfList: state.pdfs.data.mergedList,
            printedPdfList: state.pdfs.data.generatedPdfsForEncounter,
            interview: state.interview,
            encounterId: ownProps.match.params.encounterId,
            isHouseholdLoading: state.households.isInitialized === false || state.households.isLoading === true,
            person: underlyingPerson,
            contractConsent: contractService.getEffectiveContractConsent(state.interview),
            customerJurisdictions: state.customer?.data?.jurisdictions,
            shouldShowUmmcNavigation: state.interviewLookups.data?.configuration?.showDocumentationUMMCFA,
            signedPdfList: state.pdfs.data.signedPdfs,
            faxHistory: state.pdfs.data.faxHistory,
        };
    },
    dispatch => ({
        getPdfs: (assistanceCategories, customerCode, encounterId, customerId, asOf, jurisdiction) =>
            dispatch(pdfActions.getPdfList(assistanceCategories, encounterId, customerId, asOf, jurisdiction)),
        resetPdfs: () => dispatch(pdfActions.resetPdfs()),
        getDocuments: (customerId, personId) => dispatch(documentActions.getDocuments(customerId, personId)),
        getEncounterTag: encounterId => dispatch(tagsAction.getEncounterTag(encounterId)),
    })
)(DispatchComponent);
